import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Building2, Phone, Loader2, Sparkles, MapPin, Briefcase, Users2,
  Heart, LinkIcon, Send, StickyNote, User as UserIcon, Info, Globe, Tag, FileText, Clock, PhoneCall, CalendarClock,
  Pencil, X, Check,
} from 'lucide-react';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../context/AdminAuthContext';
import { getLead, updateLead, addNote, enrichLead, bookingConfig, sendBookingLink } from '../lib/marketingApi';
import { formatApiError } from '../lib/auth';
import BookTeamsCall from './BookTeamsCall';
import EnquiryReply from './EnquiryReply';

const STATUSES = [
  { value: 'new', label: 'New', cls: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'Contacted', cls: 'bg-amber-100 text-amber-700' },
  { value: 'qualified', label: 'Qualified', cls: 'bg-violet-100 text-violet-700' },
  { value: 'demo_scheduled', label: 'Demo scheduled', cls: 'bg-cyan-100 text-cyan-700' },
  { value: 'won', label: 'Won', cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'lost', label: 'Lost', cls: 'bg-rose-100 text-rose-700' },
];
const statusMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];
const SEGMENT_LABEL = {
  multifamily: 'Multifamily', commercial_industrial: 'Commercial & Industrial',
  energy_brokers: 'Energy Broker', other: 'Other',
};
const TABS = [
  { key: 'enquiry', label: 'Enquiry' },
  { key: 'reply', label: 'Reply' },
  { key: 'social', label: 'Social Profile' },
  { key: 'company', label: 'Company Profile' },
  { key: 'notes', label: 'Notes' },
];
const SEGMENT_OPTS = ['multifamily', 'commercial_industrial', 'energy_brokers', 'other'];

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); } catch (e) { return iso; }
}
const arr = (v) => (Array.isArray(v) ? v : []);
const val = (v) => (v === null || v === undefined || v === '' ? '—' : v);
const CONF_CLS = { high: 'bg-emerald-100 text-emerald-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-slate-100 text-slate-600' };

function Card({ className = '', children, ...rest }) {
  return <div className={`rounded-lg border border-slate-200 bg-white ${className}`} {...rest}>{children}</div>;
}
function CardTitle({ icon: Icon, children, right }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        {Icon && <Icon className="h-4 w-4 text-[#0035A8]" />} {children}
      </div>
      {right}
    </div>
  );
}

export default function EnquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('enquiry');
  const [owner, setOwner] = useState('');
  const [savingOwner, setSavingOwner] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [teams, setTeams] = useState([]);
  const [bookTeam, setBookTeam] = useState('');
  const [sendingLink, setSendingLink] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLead(id);
      setLead(res.lead);
      setOwner(res.lead.owner || '');
    } catch (e) {
      toast.error('Enquiry not found');
      navigate('/admin/enquiries');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { bookingConfig().then((d) => { setTeams(d.teams || []); setBookTeam(d.default_team_id || ''); }).catch(() => {}); }, []);

  const sendLink = async () => {
    setSendingLink(true);
    try {
      const res = await sendBookingLink(id, bookTeam ? { team_id: bookTeam } : {});
      setLead(res.lead);
      toast.success(`Booking link sent via ${res.team || 'default team'}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, 'Could not send booking link')); }
    finally { setSendingLink(false); }
  };

  const changeStatus = async (status) => {
    try {
      const res = await updateLead(id, { status });
      setLead(res.lead);
      toast.success('Status updated');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, 'Update failed')); }
  };
  const saveOwner = async () => {
    setSavingOwner(true);
    try {
      const res = await updateLead(id, { owner });
      setLead(res.lead);
      toast.success('Owner saved');
    } catch (e) { toast.error('Save failed'); } finally { setSavingOwner(false); }
  };
  const runEnrich = async () => {
    setEnriching(true);
    try {
      const res = await enrichLead(id);
      setLead(res.lead);
      toast.success('AI research generated');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, 'Enrichment failed')); } finally { setEnriching(false); }
  };
  const submitNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      const res = await addNote(id, noteText.trim());
      setLead(res.lead);
      setNoteText('');
    } catch (e) { toast.error('Could not add note'); } finally { setAddingNote(false); }
  };

  const startEdit = () => {
    setEdit({
      name: lead.name || '', email: lead.email || '', company: lead.company || '',
      phone: lead.phone || '', job_title: lead.job_title || '', segment: lead.segment || '',
      interest: lead.interest || '', portfolio_size: lead.portfolio_size || '', message: lead.message || '',
    });
    setEditing(true);
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const res = await updateLead(id, edit);
      setLead(res.lead);
      setEditing(false);
      toast.success('Enquiry details updated');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, 'Update failed')); }
    finally { setSavingEdit(false); }
  };

  if (loading || !lead) {
    return (
      <div className="min-h-screen bg-slate-100">
        <AdminHeader active="enquiries" />
        <div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      </div>
    );
  }

  const enr = lead.enrichment || {};
  const social = enr.social || {};
  const company = enr.company || {};
  const notes = arr(lead.notes_log);
  const enriched = !!(enr.social || enr.company);
  const sm = statusMeta(lead.status);
  const initials = (lead.name || lead.email || '?').trim().charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-slate-100" data-testid="enquiry-detail">
      <AdminHeader active="enquiries" />
      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <button onClick={() => navigate('/admin/enquiries')} data-testid="enquiry-back" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> All enquiries
        </button>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* MAIN */}
          <div className="space-y-5 lg:col-span-8">
            {/* Contact header */}
            <Card className="p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#0035A8] text-lg font-bold text-white">{initials}</span>
                  <div className="min-w-0">
                    <h1 className="text-xl font-bold tracking-tight text-slate-900">{lead.name}</h1>
                    <div className="mt-2 flex flex-col gap-1 text-sm text-slate-600">
                      <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-2 hover:text-[#0035A8]"><Mail className="h-4 w-4 text-slate-400" /> {lead.email}</a>
                      {lead.company && <span className="inline-flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" /> {lead.company}</span>}
                      {lead.phone && <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-2 hover:text-[#0035A8]"><Phone className="h-4 w-4 text-slate-400" /> {lead.phone}</a>}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`inline-flex rounded-md px-2.5 py-1 text-xs font-semibold ${sm.cls}`}>{sm.label}</span>
                  {lead.callback_requested && (
                    <span data-testid="callback-badge" className="inline-flex items-center gap-1 rounded-md bg-orange-100 px-2.5 py-1 text-xs font-semibold text-orange-700">
                      <PhoneCall className="h-3 w-3" /> Callback requested
                    </span>
                  )}
                  <div className="text-right text-xs text-slate-400">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmt(lead.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Status selector */}
              <div className="mt-5 border-t border-slate-100 pt-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Move to status</div>
                <div className="flex flex-wrap gap-1.5" data-testid="enquiry-status">
                  {STATUSES.map((s) => (
                    <button
                      key={s.value} disabled={!canEdit} onClick={() => changeStatus(s.value)}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                        lead.status === s.value ? s.cls + ' ring-2 ring-[#0035A8] ring-offset-1' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </Card>

            {/* Tabs card */}
            <Card>
              <div className="flex gap-1 border-b border-slate-200 px-3" data-testid="enquiry-tabs">
                {TABS.map((t) => (
                  <button
                    key={t.key} onClick={() => setTab(t.key)} data-testid={`enquiry-tab-${t.key}`}
                    className={`relative px-4 py-3 text-sm font-semibold transition-colors ${tab === t.key ? 'text-[#0035A8]' : 'text-slate-500 hover:text-slate-800'}`}
                  >
                    {t.label}
                    {tab === t.key && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[#0035A8]" />}
                  </button>
                ))}
              </div>

              <div className="p-5">
                {/* Enquiry */}
                {tab === 'enquiry' && (
                  <div className="space-y-4" data-testid="tab-enquiry">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Enquiry details</div>
                      {canEdit && !editing && (
                        <button onClick={startEdit} data-testid="enquiry-edit-btn" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#0035A8] hover:text-[#0035A8]">
                          <Pencil className="h-3.5 w-3.5" /> Edit details
                        </button>
                      )}
                    </div>

                    {editing ? (
                      <div className="space-y-3" data-testid="enquiry-edit-form">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <EditField label="Name" value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} testid="edit-name" />
                          <EditField label="Email" value={edit.email} onChange={(v) => setEdit({ ...edit, email: v })} testid="edit-email" />
                          <EditField label="Company" value={edit.company} onChange={(v) => setEdit({ ...edit, company: v })} testid="edit-company" />
                          <EditField label="Phone" value={edit.phone} onChange={(v) => setEdit({ ...edit, phone: v })} testid="edit-phone" />
                          <EditField label="Job title" value={edit.job_title} onChange={(v) => setEdit({ ...edit, job_title: v })} testid="edit-job-title" />
                          <EditField label="Interest" value={edit.interest} onChange={(v) => setEdit({ ...edit, interest: v })} testid="edit-interest" />
                          <EditField label="Portfolio size" value={edit.portfolio_size} onChange={(v) => setEdit({ ...edit, portfolio_size: v })} testid="edit-portfolio-size" />
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Segment</span>
                            <select value={edit.segment} onChange={(e) => setEdit({ ...edit, segment: e.target.value })} data-testid="edit-segment" className="h-9 w-full rounded-md border border-slate-300 px-2.5 text-sm outline-none focus:border-[#0035A8]">
                              <option value="">—</option>
                              {SEGMENT_OPTS.map((s) => <option key={s} value={s}>{SEGMENT_LABEL[s] || s}</option>)}
                            </select>
                          </label>
                        </div>
                        <label className="block">
                          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Message</span>
                          <textarea value={edit.message} onChange={(e) => setEdit({ ...edit, message: e.target.value })} rows={4} data-testid="edit-message" className="w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#0035A8]" />
                        </label>
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setEditing(false)} data-testid="enquiry-edit-cancel" className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 px-3.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"><X className="h-4 w-4" /> Cancel</button>
                          <button onClick={saveEdit} disabled={savingEdit} data-testid="enquiry-edit-save" className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#0035A8] px-4 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">{savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save changes</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <Field label="Interest" value={val(lead.interest)} />
                          <Field label="Portfolio size" value={val(lead.portfolio_size)} />
                          <Field label="Job title" value={val(lead.job_title)} />
                          <Field label="Segment" value={SEGMENT_LABEL[lead.segment] || val(lead.segment)} />
                          {lead.preferred_time && <Field label="Preferred time" value={lead.preferred_time} />}
                        </div>
                        {lead.message ? (
                          <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                            <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><FileText className="h-3.5 w-3.5" /> Message</div>
                            <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{lead.message}</p>
                          </div>
                        ) : (
                          <div className="rounded-lg border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400">No message submitted.</div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Reply — AI-drafted, sent from the rep's own M365 mailbox */}
                {tab === 'reply' && (
                  <div data-testid="tab-reply">
                    <EnquiryReply lead={lead} canEdit={canEdit} onSent={load} />
                  </div>
                )}

                {/* Social */}
                {tab === 'social' && (
                  <div data-testid="tab-social" className="space-y-4">
                    {!enr.social ? (
                      <ProfileEmpty enriching={enriching} text="No social profile yet. Use AI Research in the right panel to generate a best-effort profile from the enquiry details." />
                    ) : (
                      <>
                        <ConfBadge confidence={social.confidence} />
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <IconField icon={Briefcase} label="Headline" value={val(social.headline)} />
                          <IconField icon={MapPin} label="Location" value={val(social.location)} />
                          <IconField icon={UserIcon} label="Current role" value={val(social.current_role)} />
                          <IconField icon={Building2} label="Current company" value={val(social.current_company)} />
                          <IconField icon={Users2} label="Reports to" value={val(social.reports_to)} />
                          <IconField icon={Info} label="Seniority" value={val(social.seniority)} />
                        </div>
                        <ChipRow icon={Briefcase} label="Past roles" items={arr(social.past_roles).map((r) => `${r.title || ''}${r.company ? ' @ ' + r.company : ''}`.trim())} />
                        <ChipRow icon={Heart} label="Hobbies" items={arr(social.hobbies)} />
                        <ChipRow icon={Sparkles} label="Interests" items={arr(social.interests)} />
                        {arr(social.social_links).length > 0 && (
                          <div>
                            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><LinkIcon className="h-3.5 w-3.5" /> Links</div>
                            <div className="flex flex-wrap gap-2">
                              {arr(social.social_links).map((l, i) => (
                                <a key={i} href={l.url} target="_blank" rel="noreferrer" className="rounded-md bg-slate-100 px-3 py-1 text-xs text-slate-700 hover:bg-slate-200">{l.platform || 'link'}</a>
                              ))}
                            </div>
                          </div>
                        )}
                        {social.summary && <SummaryBox text={social.summary} />}
                        {enr.generated_at && <div className="pt-1 text-[11px] text-slate-400">Generated {fmt(enr.generated_at)}</div>}
                      </>
                    )}
                  </div>
                )}

                {/* Company */}
                {tab === 'company' && (
                  <div data-testid="tab-company" className="space-y-4">
                    {!enr.company ? (
                      <ProfileEmpty enriching={enriching} text="No company profile yet. Use AI Research in the right panel to generate a best-effort profile from the enquiry details." />
                    ) : (
                      <>
                        <ConfBadge confidence={company.confidence} />
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <IconField icon={Building2} label="Company" value={val(company.name)} />
                          <IconField icon={Briefcase} label="Industry" value={val(company.industry)} />
                          <IconField icon={Users2} label="Size" value={val(company.size)} />
                          <IconField icon={MapPin} label="HQ" value={val(company.hq)} />
                          <IconField icon={LinkIcon} label="Website" value={val(company.website)} />
                          <IconField icon={Info} label="Founded" value={val(company.founded)} />
                        </div>
                        {company.description && <SummaryBox label="Description" text={company.description} />}
                        <ChipRow icon={Sparkles} label="Products" items={arr(company.products)} />
                        {arr(company.related_contacts).length > 0 && (
                          <div>
                            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Users2 className="h-3.5 w-3.5" /> Related contacts</div>
                            <div className="space-y-1.5">
                              {arr(company.related_contacts).map((c, i) => (
                                <div key={i} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm">
                                  <span className="font-medium text-slate-800">{c.name}</span>
                                  <span className="text-slate-500">{c.title}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {company.summary && <SummaryBox text={company.summary} />}
                        {enr.generated_at && <div className="pt-1 text-[11px] text-slate-400">Generated {fmt(enr.generated_at)}</div>}
                      </>
                    )}
                  </div>
                )}

                {/* Notes */}
                {tab === 'notes' && (
                  <div data-testid="tab-notes">
                    {canEdit && (
                      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} data-testid="note-input" placeholder="Add a note about your outreach…" className="w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0035A8]" />
                        <div className="mt-2 flex justify-end">
                          <button onClick={submitNote} disabled={addingNote || !noteText.trim()} data-testid="note-submit" className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#0035A8] px-4 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60"><Send className="h-4 w-4" /> Add note</button>
                        </div>
                      </div>
                    )}
                    {notes.length === 0 ? (
                      <div className="flex flex-col items-center gap-2 py-12 text-slate-400"><StickyNote className="h-8 w-8" /><p className="text-sm">No notes yet.</p></div>
                    ) : (
                      <div className="space-y-3" data-testid="notes-list">
                        {[...notes].reverse().map((n) => (
                          <div key={n.id} className="rounded-lg border border-slate-100 bg-white p-4">
                            <div className="mb-1 flex items-center justify-between text-xs text-slate-400">
                              <span className="font-medium text-slate-600">{n.author}</span>
                              <span>{fmt(n.created_at)}</span>
                            </div>
                            <p className="whitespace-pre-wrap text-sm text-slate-700">{n.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* SIDEBAR */}
          <aside className="space-y-5 lg:col-span-4">
            {/* AI Research */}
            <Card>
              <CardTitle icon={Sparkles}>AI Research</CardTitle>
              <div className="space-y-3 p-5">
                <div className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {enr.disclaimer || 'AI-generated best-effort estimate. Verify before use.'}
                </div>
                {enriched && (
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    {social.confidence && <ConfBadge confidence={social.confidence} prefix="Social" inline />}
                    {company.confidence && <ConfBadge confidence={company.confidence} prefix="Company" inline />}
                  </div>
                )}
                {canEdit ? (
                  <button onClick={runEnrich} disabled={enriching} data-testid="enrich-btn" className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md bg-[#0035A8] text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">
                    {enriching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {enriched ? 'Regenerate research' : 'Generate with AI'}
                  </button>
                ) : (
                  <p className="text-xs text-slate-400">Read-only — ask an editor to generate research.</p>
                )}
                {enr.generated_at && <div className="text-center text-[11px] text-slate-400">Last run {fmt(enr.generated_at)}</div>}
              </div>
            </Card>

            {/* Book a call */}
            <Card>
              <CardTitle icon={CalendarClock}>Book a call</CardTitle>
              <div className="space-y-3 p-5">
                {lead.booking_link_sent_at && (
                  <div className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700">Booking link last sent {fmt(lead.booking_link_sent_at)}</div>
                )}
                {teams.length === 0 ? (
                  <p className="text-xs text-slate-500">No scheduler configured yet. Add one in <span className="font-semibold">Settings → Booking &amp; Scheduling</span>.</p>
                ) : (
                  <>
                    <select value={bookTeam} onChange={(e) => setBookTeam(e.target.value)} data-testid="enquiry-book-team" className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-[#0035A8]">
                      {teams.map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (default)' : ''}</option>)}
                    </select>
                    {canEdit ? (
                      <button onClick={sendLink} disabled={sendingLink} data-testid="enquiry-send-booking-link" className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md bg-[#0035A8] text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">
                        {sendingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send booking link
                      </button>
                    ) : <p className="text-xs text-slate-400">Read-only — ask an editor to send a link.</p>}
                  </>
                )}
              </div>
            </Card>

            {/* Book a Teams call (Microsoft 365) */}
            <Card>
              <CardTitle icon={CalendarClock}>Book a Teams call</CardTitle>
              <BookTeamsCall lead={lead} canEdit={canEdit} onBooked={load} />
            </Card>

            {/* Owner */}
            <Card>
              <CardTitle icon={UserIcon}>Owner</CardTitle>
              <div className="p-5">
                <div className="flex gap-2">
                  <input value={owner} onChange={(e) => setOwner(e.target.value)} disabled={!canEdit} data-testid="enquiry-owner" placeholder="Assign owner…" className="h-10 flex-1 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-[#0035A8] disabled:bg-slate-50" />
                  {canEdit && <button onClick={saveOwner} disabled={savingOwner} className="rounded-md bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-60">{savingOwner ? '…' : 'Save'}</button>}
                </div>
              </div>
            </Card>

            {/* Source & tracking */}
            <Card>
              <CardTitle icon={Tag}>Source &amp; tracking</CardTitle>
              <dl className="divide-y divide-slate-100">
                <MetaRow icon={Tag} label="Source" value={val(lead.source)} />
                <MetaRow icon={FileText} label="Page" value={val(lead.page)} />
                <MetaRow icon={Globe} label="Language" value={(lead.language || 'en').toUpperCase()} />
                <MetaRow icon={Sparkles} label="UTM campaign" value={val(lead.utm_campaign)} />
              </dl>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-100 p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-slate-800">{value}</div>
    </div>
  );
}
function EditField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-[#0035A8]" />
    </label>
  );
}
function IconField({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-slate-100 p-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
        <div className="truncate text-sm font-medium text-slate-800">{value}</div>
      </div>
    </div>
  );
}
function MetaRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 py-2.5">
      <span className="inline-flex items-center gap-2 text-sm text-slate-500"><Icon className="h-3.5 w-3.5 text-slate-400" /> {label}</span>
      <span className="truncate text-sm font-medium text-slate-800">{value}</span>
    </div>
  );
}
function ChipRow({ icon: Icon, label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className="flex flex-wrap gap-2">
        {items.filter(Boolean).map((it, i) => (<span key={i} className="rounded-md bg-slate-100 px-3 py-1 text-xs text-slate-700">{it}</span>))}
      </div>
    </div>
  );
}
function SummaryBox({ label = 'Summary', text }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{text}</p>
    </div>
  );
}
function ConfBadge({ confidence, prefix, inline }) {
  if (!confidence) return null;
  return (
    <span data-testid={inline ? undefined : 'confidence-badge'} className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold ${CONF_CLS[confidence] || CONF_CLS.low}`}>
      {prefix ? `${prefix}: ${confidence}` : `Confidence: ${confidence}`}
    </span>
  );
}
function ProfileEmpty({ enriching, text }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-slate-200 py-12 text-center">
      <Sparkles className="h-8 w-8 text-slate-300" />
      <p className="max-w-sm text-sm text-slate-500">{text}</p>
      {enriching && <Loader2 className="h-5 w-5 animate-spin text-[#0035A8]" />}
    </div>
  );
}
